/**
 * ESCOLHA COMO CONTINUAR (CERES-008 §4; contexto neutro na
 * CERES-ADM-004-CORRECAO-02).
 *
 * Aparece para quem tem MAIS de uma organização — quem tem uma entra direto —
 * e para quem NÃO TEM organização ativa nenhuma: a sessão existe, mas está no
 * contexto neutro, que só alcança a própria sessão, os próprios convites e a
 * escolha. A escolha vale para a SESSÃO e o servidor recusa qualquer rota de
 * dados enquanto ela não existir: esta tela é o caminho, não o bloqueio.
 *
 * Os convites aparecem aqui porque, sem organização ativa, é o único lugar
 * aonde a pessoa consegue chegar. Aceitar NÃO entra na organização: ela passa
 * a constar da lista, e entrar é um segundo clique, explícito.
 *
 * Fora do AppShell de propósito, como as demais telas de sessão restrita:
 * oferecer menus aqui prometeria acesso que a API vai negar.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SimboloCeres } from '../components/Marca';
import { ErrorState, IconBuilding, IconShield, InlineError, LoadingRows } from '../components/ui';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useConfiguracaoPublica } from '../lib/configuracao-publica';
import { t } from '../lib/i18n';

interface Convite {
  organizationId: string;
  organizacao: string;
  convidadoPor: string | null;
  perfisPropostos?: string[];
  convidadoEm?: string;
  expiraEm: string | null;
  vencido: boolean;
}

interface Confirmacao {
  tipo: 'aceito' | 'recusado';
  organizationId: string;
  organizacao: string;
}

export function EscolherOrganizacaoPage(): JSX.Element {
  const { user, logout, trocarOrganizacao, entrarNaPlataforma, refresh } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [entrandoEm, setEntrandoEm] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);

  const { config } = useConfiguracaoPublica();
  const organizacoes = user?.organizations ?? [];
  // CERES-ADM-002: quem tem papel de plataforma escolhe também a
  // Administração da plataforma — explicitamente, como escolhe um tenant.
  const papelDePlataforma = user?.platformRole ?? null;

  const convites = useQuery({
    queryKey: ['convites-recebidos'],
    queryFn: () => api.get<{ data: Convite[] }>('/auth/convites'),
  });
  const listaDeConvites = convites.data?.data ?? [];

  const responder = useMutation({
    mutationFn: (e: { convite: Convite; acao: 'aceitar' | 'recusar' }) =>
      api.post(`/auth/convites/${e.convite.organizationId}/${e.acao}`),
    onMutate: () => {
      setErro(null);
      setConfirmacao(null);
    },
    onSuccess: async (_r, e) => {
      setConfirmacao({
        tipo: e.acao === 'aceitar' ? 'aceito' : 'recusado',
        organizationId: e.convite.organizationId,
        organizacao: e.convite.organizacao,
      });
      await queryClient.invalidateQueries({ queryKey: ['convites-recebidos'] });
      // A lista de organizações da sessão muda com o aceite — e só ela: a
      // sessão continua onde estava até a pessoa escolher entrar.
      await refresh();
    },
    onError: (e) => setErro(e),
  });

  const entrar = async (organizationId: string): Promise<void> => {
    setErro(null);
    setEntrandoEm(organizationId);
    try {
      await trocarOrganizacao(organizationId);
      navigate('/', { replace: true });
    } catch (e) {
      setErro(e);
      setEntrandoEm(null);
    }
  };

  const entrarNaAdministracao = async (): Promise<void> => {
    setErro(null);
    setEntrandoEm('plataforma');
    try {
      await entrarNaPlataforma();
      navigate('/admin/plataforma', { replace: true });
    } catch (e) {
      setErro(e);
      setEntrandoEm(null);
    }
  };

  const sair = async (): Promise<void> => {
    await logout();
    navigate('/entrar', { replace: true });
  };

  const ocupado = entrandoEm !== null || responder.isPending;
  const semOrganizacao = organizacoes.length === 0;
  const convitesValidos = listaDeConvites.filter((c) => !c.vencido).length;
  const semOpcao =
    semOrganizacao && papelDePlataforma === null && convites.isSuccess && listaDeConvites.length === 0;

  const subtitulo = semOpcao
    ? null
    : semOrganizacao
      ? convitesValidos > 0
        ? t('ctx.subtituloComConvites')
        : papelDePlataforma !== null
          ? t('plat.escolha.soPlataforma')
          : t('ctx.subtituloConvitesVencidos')
      : t('org.escolherSubtitulo');

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-sunken p-4">
      <div className="w-full max-w-lg rounded-xl border border-line bg-surface p-5 shadow-low sm:p-6">
        <SimboloCeres className="h-6 w-6 text-brand-600" decorativa />
        <h1 className="mt-4 text-base font-semibold text-ink">{t('ctx.titulo')}</h1>
        {subtitulo !== null && <p className="mt-1 text-xs text-ink-muted">{subtitulo}</p>}

        <InlineError error={erro} />

        {confirmacao !== null && (
          <div role="status" aria-live="polite" className="mt-4 rounded-lg border border-line bg-surface-sunken p-3 text-sm text-ink">
            {confirmacao.tipo === 'aceito' ? (
              <>
                <p className="font-medium">{t('ctx.aceito')}</p>
                <p className="mt-0.5 text-xs text-ink-muted">{confirmacao.organizacao}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    disabled={ocupado}
                    onClick={() => void entrar(confirmacao.organizationId)}
                  >
                    {entrandoEm === confirmacao.organizationId ? t('org.trocando') : t('ctx.entrarNaOrganizacao')}
                  </button>
                  <button type="button" className="btn-secondary btn-sm" disabled={ocupado} onClick={() => setConfirmacao(null)}>
                    {t('ctx.voltarAEscolha')}
                  </button>
                </div>
              </>
            ) : (
              <p>{t('ctx.recusado')}</p>
            )}
          </div>
        )}

        {/* Organizações disponíveis e a Administração da plataforma. */}
        {(papelDePlataforma !== null || !semOrganizacao) && (
          <ul className="mt-4 space-y-2" aria-label={t('ctx.opcoes')}>
            {papelDePlataforma !== null && (
              <li>
                <button
                  type="button"
                  onClick={() => void entrarNaAdministracao()}
                  disabled={ocupado}
                  className="flex w-full items-center gap-3 rounded-lg border border-ink bg-ink p-3 text-left text-ink-inverse transition hover:opacity-90 disabled:opacity-60"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-white/10">
                    <IconShield className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{t('plat.titulo')}</span>
                    <span className="block truncate text-2xs text-white/70">{t('plat.escolha.semTenant')}</span>
                  </span>
                  <span className="text-xs text-accent-300">
                    {entrandoEm === 'plataforma' ? t('org.trocando') : t('org.entrar')}
                  </span>
                </button>
              </li>
            )}
            {organizacoes.map((organizacao) => (
              <li key={organizacao.id}>
                <button
                  type="button"
                  onClick={() => void entrar(organizacao.id)}
                  disabled={ocupado}
                  className="flex w-full items-center gap-3 rounded-lg border border-line bg-surface p-3 text-left transition hover:border-brand-300 disabled:opacity-60"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-surface-sunken text-ink-muted">
                    <IconBuilding className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{organizacao.name}</span>
                  </span>
                  <span className="text-xs text-brand-600">
                    {entrandoEm === organizacao.id ? t('org.trocando') : t('org.entrar')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Convites recebidos — os da própria pessoa, e só eles. */}
        {convites.isLoading ? (
          <div className="mt-4">
            <LoadingRows rows={1} columns={2} />
          </div>
        ) : convites.isError ? (
          <div className="mt-4">
            <ErrorState error={convites.error} onRetry={() => void convites.refetch()} />
          </div>
        ) : listaDeConvites.length > 0 ? (
          <section className="mt-5" aria-labelledby="convites-titulo">
            <h2 id="convites-titulo" className="text-sm font-semibold text-ink">
              {semOrganizacao ? t('ctx.convitesDestaque') : t('org.convites.titulo')}
            </h2>
            <ul className="mt-2 space-y-2">
              {listaDeConvites.map((c) => (
                <li key={c.organizationId} className="rounded-lg border border-line p-3">
                  <p className="text-sm font-medium text-ink">{c.organizacao}</p>
                  {(c.perfisPropostos?.length ?? 0) > 0 && (
                    <p className="mt-0.5 text-xs text-ink">
                      {t('org.convites.perfilProposto')} {c.perfisPropostos?.join(', ')}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {c.convidadoPor !== null && `${t('org.convites.por')} ${c.convidadoPor}`}
                    {c.convidadoPor !== null && c.convidadoEm !== undefined && ' · '}
                    {c.convidadoEm !== undefined && `${t('ctx.convidadoEm')} ${formatDateTime(c.convidadoEm)}`}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {c.vencido
                      ? t('ctx.vencidoOrientacao')
                      : `${t('org.convites.valeAte')} ${c.expiraEm === null ? '—' : formatDateTime(c.expiraEm)}`}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {!c.vencido && (
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        disabled={ocupado}
                        onClick={() => responder.mutate({ convite: c, acao: 'aceitar' })}
                      >
                        {t('org.convites.aceitar')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={ocupado}
                      onClick={() => responder.mutate({ convite: c, acao: 'recusar' })}
                    >
                      {t('org.convites.recusar')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Conta sem nenhuma opção: explica, sem erro técnico nem laço. */}
        {semOpcao && (
          <div className="mt-4 rounded-lg border border-line bg-surface-sunken p-4 text-sm text-ink">
            <p className="font-medium">{t('ctx.semOpcao.titulo')}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-ink-muted">
              <li>{t('ctx.semOpcao.contaExiste')}</li>
              <li>{t('ctx.semOpcao.semOrganizacao')}</li>
              <li>{t('ctx.semOpcao.semConvite')}</li>
              <li>{t('ctx.semOpcao.peca')}</li>
            </ul>
          </div>
        )}

        {/* CERES-ACC-005-OPER-001: fundar a própria empresa, quando o cadastro
            está aberto e a identidade não é da administração da plataforma. */}
        {config.cadastroPublicoHabilitado && papelDePlataforma === null && (
          <Link to="/nova-empresa" className="btn-secondary btn-sm mt-4 inline-flex">
            {t('outraOrganizacao.link')}
          </Link>
        )}

        <button
          type="button"
          onClick={() => void sair()}
          disabled={entrandoEm !== null}
          className="mt-5 block text-xs text-ink-subtle underline hover:text-ink"
        >
          {t('org.sair')}
        </button>
      </div>
    </main>
  );
}
